import { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import MDBox from "components/MDBox";

function EventDetailsDialog({
  event,
  events,
  attributionUrl,
  commonFields = [],
  compactInlineFields,
  contextFlagSrc,
  contextText,
  dateLabel,
  formatDate,
  fields,
  getEventDetails,
  getEventIcon,
  getEventSx,
  getEventSummary,
  getEventTitle,
  getNavigationKey,
  getRelatedEvents,
  inlineFields,
  onClose,
  onNavigate,
  title,
}) {
  const { t, i18n } = useTranslation();
  const navigableEvents = useMemo(() => {
    if (!event) return [];

    const navigationKey = getNavigationKey ? getNavigationKey(event) : undefined;
    const eventByDate = new Map();

    events.map(getEventDetails).forEach((candidate) => {
      if (!candidate?.date || eventByDate.has(candidate.date)) return;
      if (navigationKey !== undefined && getNavigationKey(candidate) !== navigationKey) return;
      eventByDate.set(candidate.date, candidate);
    });

    return [...eventByDate.values()].sort((first, second) => first.date.localeCompare(second.date));
  }, [event, events, getEventDetails, getNavigationKey]);

  const currentIndex = navigableEvents.findIndex((candidate) => candidate.date === event?.date);
  const previousEvent = currentIndex > 0 ? navigableEvents[currentIndex - 1] : null;
  const nextEvent = currentIndex >= 0 && currentIndex < navigableEvents.length - 1
    ? navigableEvents[currentIndex + 1]
    : null;
  const displayedEvents = useMemo(() => {
    if (!event) return [];

    return getRelatedEvents ? getRelatedEvents(event, events) : [event];
  }, [event, events, getRelatedEvents]);

  return (
    <Dialog
      open={Boolean(event)}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{ sx: { width: "600px", maxWidth: "calc(100% - 32px)" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        {title}
        <MDBox display="flex" alignItems="center">
          <Tooltip title={t("calendar.previousDate")}>
            <span>
              <IconButton
                aria-label={t("calendar.previousDate")}
                disabled={!previousEvent}
                onClick={() => onNavigate(previousEvent)}
                size="small"
                sx={{ color: "info.main" }}
              >
                <Icon>chevron_left</Icon>
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t("calendar.nextDate")}>
            <span>
              <IconButton
                aria-label={t("calendar.nextDate")}
                disabled={!nextEvent}
                onClick={() => onNavigate(nextEvent)}
                size="small"
                sx={{ color: "info.main" }}
              >
                <Icon>chevron_right</Icon>
              </IconButton>
            </span>
          </Tooltip>
        </MDBox>
      </DialogTitle>
      <DialogContent dividers>
        {event ? (
          <Stack spacing={1.5}>
            {dateLabel ? (
              <MDBox
                display="flex"
                alignItems="baseline"
                gap={1}
                px={0.5}
                sx={{ color: "text.secondary" }}
              >
                <MDBox component="span">{dateLabel}</MDBox>
                <MDBox component="strong">{formatDate(event.date)}</MDBox>
              </MDBox>
            ) : null}
            {contextText ? (
              <MDBox display="flex" alignItems="center" gap={0.75} px={0.5} sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                {contextFlagSrc ? (
                  <MDBox component="img" src={contextFlagSrc} alt="" sx={{ width: 20, height: 15, objectFit: "cover" }} />
                ) : null}
                {contextText}
              </MDBox>
            ) : null}
            {commonFields.length ? (
              <MDBox
                width="100%"
                p={1.5}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                  backgroundColor: "grey.100",
                  borderRadius: 1,
                }}
              >
                {commonFields.map(({ icon, label, value }) => (
                  <MDBox
                    key={label}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{ flex: { xs: "1 1 100%", sm: "1 1 0" }, minWidth: 0, flexWrap: "nowrap" }}
                  >
                    <Icon color="info" fontSize="small">{icon}</Icon>
                    <MDBox component="strong" sx={{ whiteSpace: "nowrap" }}>{label}</MDBox>
                    <MDBox component="span" sx={{ whiteSpace: "nowrap" }}>{value(event)}</MDBox>
                  </MDBox>
                ))}
              </MDBox>
            ) : null}
            {attributionUrl ? (
              <MDBox sx={{ color: "text.secondary", fontSize: "0.75rem", textAlign: "left" }}>
                Powered by ලක් හඳහන | {" "}
                <MDBox
                  component="a"
                  href={attributionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: "info.main", fontWeight: 600, textDecoration: "none" }}
                >
                  app.lakhandahana.com
                </MDBox>
              </MDBox>
            ) : null}
            <MDBox pr={0.5} sx={{ maxHeight: "42vh", overflowY: "auto" }}>
              <Stack spacing={1.5}>
                {displayedEvents.map((item, index) => (
                  <MDBox
                    key={`${item.date}-${item.period || item.title}-${item.startTime || index}`}
                    width="100%"
                    p={2}
                    sx={{ borderRadius: 1, ...getEventSx?.(item) }}
                  >
                    {displayedEvents.length > 1 ? (
                      <MDBox
                        mb={1}
                        pb={0.75}
                        display="flex"
                        alignItems="center"
                        gap={0.75}
                        fontWeight="fontWeightBold"
                        sx={{ borderBottom: "1px solid #d9d9d9" }}
                      >
                        {getEventIcon ? <Icon fontSize="small">{getEventIcon(item)}</Icon> : null}
                        {getEventTitle?.(item) || item.title}
                      </MDBox>
                    ) : null}
                    <MDBox
                      display="flex"
                      alignItems="baseline"
                      gap={1.5}
                      sx={{ flexDirection: inlineFields ? "row" : "column", flexWrap: inlineFields ? "nowrap" : "wrap" }}
                    >
                      {fields.map(({ label, value }) => (
                        <MDBox
                          key={label}
                          display="flex"
                          alignItems="baseline"
                          gap={1}
                          sx={{ flex: inlineFields && !compactInlineFields ? "1 1 0" : "0 0 auto", minWidth: 0, flexWrap: "nowrap" }}
                        >
                          {i18n.language.startsWith("si") ? (
                            <>
                              <MDBox component="strong" sx={{ flex: compactInlineFields ? "0 0 auto" : 1, whiteSpace: "nowrap" }}>{value(item)}</MDBox>
                              <MDBox component="span" sx={{ whiteSpace: "nowrap" }}>{label}</MDBox>
                            </>
                          ) : (
                            <>
                              <MDBox component="span" sx={{ whiteSpace: "nowrap" }}>{label}</MDBox>
                              <MDBox component="strong" sx={{ flex: compactInlineFields ? "0 0 auto" : 1, whiteSpace: "nowrap" }}>{value(item)}</MDBox>
                            </>
                          )}
                        </MDBox>
                      ))}
                    </MDBox>
                    {getEventSummary ? (
                      <MDBox mt={1} fontWeight="fontWeightBold">{getEventSummary(item)}</MDBox>
                    ) : null}
                  </MDBox>
                ))}
              </Stack>
            </MDBox>
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("calendar.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}

EventDetailsDialog.propTypes = {
  event: PropTypes.shape({
    date: PropTypes.string,
  }),
  events: PropTypes.arrayOf(PropTypes.object).isRequired,
  attributionUrl: PropTypes.string,
  commonFields: PropTypes.arrayOf(PropTypes.shape({
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.func.isRequired,
  })),
  compactInlineFields: PropTypes.bool,
  contextFlagSrc: PropTypes.string,
  contextText: PropTypes.string,
  dateLabel: PropTypes.string,
  formatDate: PropTypes.func,
  fields: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.func.isRequired,
  })).isRequired,
  getEventDetails: PropTypes.func.isRequired,
  getEventIcon: PropTypes.func,
  getEventSx: PropTypes.func,
  getEventSummary: PropTypes.func,
  getEventTitle: PropTypes.func,
  getNavigationKey: PropTypes.func,
  getRelatedEvents: PropTypes.func,
  inlineFields: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  title: PropTypes.node,
};

EventDetailsDialog.defaultProps = {
  event: null,
  attributionUrl: "",
  commonFields: [],
  compactInlineFields: false,
  contextFlagSrc: "",
  contextText: "",
  dateLabel: "",
  formatDate: (date) => date,
  getEventIcon: undefined,
  getEventSx: undefined,
  getEventSummary: undefined,
  getEventTitle: undefined,
  getNavigationKey: undefined,
  getRelatedEvents: undefined,
  inlineFields: false,
  title: "",
};

export default EventDetailsDialog;